import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import { UserActive } from "api/rpc";
import { GetCustomerPaymentMethods } from "api/rpc/2024-04/customer/customer";
import { GetCustomerReservationSegment, PostReservationBooking } from "api/rpc/2024-04/customer/reservation";
import axios, { CancelToken } from "axios";
import Card from "components/card/Card";
import Spin from "components/spin/spin";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import moment from "moment";
import { ICreditCard } from "pages/guest/Customer/CustomerProfile";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { clientPortalUpdate } from "redux/actions/clientPortal";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { ISegment } from "redux/reducers/models/reservations";
import { ClientPortalNavigation } from "../../ClientPortalNavigation";
import { Select } from "components/select/index";
import "./clientPortalReservationBooking.scss";
import Radio from "components/radio";
import DataTable from "components/dataTable/DataTable";
import { LocaleCurrency } from "helpers/Locale";
import { capitalize } from "helpers/Helpers";
import Checkbox from "components/form/checkbox/Checkbox";
import { ButtonNew as Button } from "components/buttonNew/index";
import ButtonGroup from "components/button/ButtonGroup";
import useModal from "hooks/modals/useModal";
import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import CardSection from "components/cardSection/CardSection";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import Portal from "elements/Portal";
import Markdown from "components/markdown/Markdown";
import { PostPaymentMethod, PostSetup } from "api/rpc/2024-04/customer/paymentMethod";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import Panel from "components/panel/Panel";

/**
 * @property {string}
 * @description key is the name of the duration and the value is an array of segment ids.
 * @example
 * {
 *
 * '0.5 Hour': [10], '1 Hour': [10,11], '1.5 Hour': [10,11,12]
 *
 * }
 *  */
interface IDuration {
  [key: string]: Array<number>;
}

interface IState {
  customerCreditCards: Array<ICreditCard>;
  selectedCardId: number;
  segments: Array<ISegment>;
  durations: IDuration;
  /**Object containing arrays of the segment objects (used for displaying the time and price of the selected segments) */
  durationSegments: { [key: string]: Array<ISegment> };
  durationKey: string;
  guests: number;
  acceptTerms: boolean;
  elementComplete: {
    cardNumber: boolean;
    cardExpiry: boolean;
    cardCvc: boolean;
  };
  saveCard: boolean;
  endTime: string;
}

interface IParams {
  clientShortName: string;
  facilityShortName: string;
}

const MINUTES_PER_SEGMENT = 30;

export default function ClientPortalReservationBooking() {
  const { clientPortalStore } = useAppSelector(store => store);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const { clientShortName, facilityShortName } = useParams<IParams>();
  const { Option } = Select;
  const windowSize = useWindowSize();

  const [state, setState] = useState<IState>({
    customerCreditCards: [],
    selectedCardId: null,
    segments: [],
    durations: null,
    durationKey: "0.5 Hour",
    durationSegments: null,
    guests: 1,
    acceptTerms: false,
    elementComplete: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    },
    saveCard: false,
    endTime: "",
  });

  const { state: newCardState, updateModal: updateNewCardModal, closeModal: closeNewCardModal } = useModal();
  const {
    state: paymentTermsState,
    updateModal: updatePaymentTermsModal,
    closeModal: closePaymentTermsModal,
  } = useModal();

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (
      clientPortalStore?.selectedFacility?.client_id &&
      clientPortalStore?.reservation?.segment?.credit_card_required
    ) {
      void getCustomerPaymentMethods(clientPortalStore?.selectedFacility?.client_id, source.token);
    }
    void checkActiveUser();
    if (clientPortalStore?.reservation) {
      void getSegments(source.token);
    }
    return () => {
      source.cancel();
    };
  }, []);

  useEffect(() => {
    if (state?.durations && state?.segments && state?.durationKey) {
      void setEndTime();
    }
  }, [state?.durations, state?.durationKey, state?.segments]);

  function checkActiveUser() {
    if (!clientPortalStore?.user) {
      void navigateToReservations();
    }
  }

  function navigateToReservations() {
    dispatch(clientPortalUpdate({ reservation: null }));
    history.push(`/portal/${clientShortName}/reservations/${facilityShortName ?? ""}`);
  }

  async function getSegments(cancelToken: CancelToken) {
    const segmentsRes = await GetCustomerReservationSegment(
      {
        module_id: clientPortalStore?.reservation?.module?.id,
        date: clientPortalStore?.reservation?.segment?.date,
        start_time: clientPortalStore?.reservation?.segment?.start_time,
        location_id: clientPortalStore?.reservation?.location?.id,
      },
      true,
    );
    if (segmentsRes?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken.reason) {
        return;
      }
      dispatch(showError(t("Error getting segments")));
      return;
    }

    void setDurations(segmentsRes?.data);

    setState(prevState => ({ ...prevState, segments: segmentsRes?.data }));
  }

  function setEndTime() {
    let minutes = 0;
    let hours = 0;
    state?.segments?.every((segment: ISegment, i: number) => {
      minutes += MINUTES_PER_SEGMENT;
      if (state?.durations[state?.durationKey]?.includes(segment?.id)) {
        hours = minutes / 60;
        return true;
      } else {
        return false;
      }
    });
    const endTime = moment(
      `${clientPortalStore?.reservation.segment?.date} ${clientPortalStore?.reservation.segment?.start_time}`,
    )
      .add(hours, "hours")
      .format("HH:mm:ss");
    setState(prevState => ({ ...prevState, endTime: endTime }));
  }

  function setDurations(segments: Array<ISegment>) {
    //Can only select segments from index 0-9 for 0.5 - 5 hours with 0.5 hour increments
    const max = Math.min(10, segments?.length);
    let minutes = 0;
    let hours = 0;

    const durations: { [key: string]: Array<number> } = {};
    const durationSegments: { [key: string]: Array<ISegment> } = {};

    for (let i = 0; i < max; i++) {
      minutes += MINUTES_PER_SEGMENT;
      if (segments[i]?.blocked_type === "open" && segments[i]?.booking_id === null) {
        hours = minutes / 60;
        const timeKey = `${hours} ${hours > 1 ? "Hours" : "Hour"}`;
        //Set the durations object keys as the hours and the segment ids array as the value
        durations[timeKey] = segments
          ?.filter((filteredSegment: ISegment, index: number) => index <= i)
          ?.map((segment: ISegment) => segment?.id);
        durationSegments[timeKey] = segments?.filter((filteredSegment: ISegment, index: number) => index <= i);
      } else {
        break;
      }
    }

    //If no duration times are available to select, close the booking modal
    if (Object.keys(durations)?.length === 0) {
      dispatch(showError(t("Time unavailable, please select another time")));
      void navigateToReservations();
      return;
    }

    const durationKey = Object.keys(durations)[0];

    setState(prevState => ({ ...prevState, durations, durationSegments, durationKey }));
  }

  async function getCustomerPaymentMethods(clientId: number, cancelToken: CancelToken) {
    const customerPaymentMethodsRes = await GetCustomerPaymentMethods(
      { client_id: clientId, type: ["card"] },
      true,
      cancelToken,
    );
    if (customerPaymentMethodsRes.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      dispatch(showError(t("Error getting customer payment methods")));
      return;
    }

    setState(prevState => ({
      ...prevState,
      customerCreditCards: customerPaymentMethodsRes.data?.card,
      selectedCardId:
        customerPaymentMethodsRes.data?.card?.length >= 1 ? customerPaymentMethodsRes.data?.card[0]?.id : undefined,
    }));
  }

  function handleDropDownChange(value: string | number, id: string) {
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleCardChange(cardId: number) {
    setState(prevState => ({ ...prevState, selectedCardId: cardId }));
  }

  function handleUpdateGuestsAmount(amount: number) {
    setState(prevState => ({ ...prevState, guests: amount }));
  }

  function handleCheckboxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = e.target;
    setState(prevState => ({ ...prevState, [id]: checked }));
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleCardSectionChange(
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) {
    setState(prevState => ({
      ...prevState,
      elementComplete: { ...prevState.elementComplete, [e.elementType]: e.complete },
    }));
  }

  async function handleAddNewCard() {
    if (!stripe || !elements) {
      dispatch(showError(t("Error occured before card could save. Please reattempt.")));
      return;
    }
    const tempCreditCards = [...state.customerCreditCards];
    dispatch(enqueue());
    try {
      const stripePaymentMethodRes = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement("cardNumber"),
      });

      if (stripePaymentMethodRes.paymentMethod) {
        const setupRes = await PostSetup(false);

        if (setupRes.status !== StatusCode.OK) {
          dispatch(showError(t("Error adding payment method")));
        }
        if (stripePaymentMethodRes.paymentMethod.id !== "undefined") {
          const stripeConfirmCardRes = await stripe.confirmCardSetup(setupRes.data.setup_intent.client_secret, {
            payment_method: stripePaymentMethodRes.paymentMethod.id,
          });
          if (stripeConfirmCardRes.error) {
            dispatch(showError(stripeConfirmCardRes.error.message));
          } else {
            const postPaymentRes = await PostPaymentMethod(
              {
                payment_method_id: stripeConfirmCardRes.setupIntent.payment_method,
                facility_id: clientPortalStore?.selectedFacility?.id,
                save_card: state.saveCard,
              },
              false,
            );
            if (postPaymentRes.status !== StatusCode.OK) {
              dispatch(showError(t("Error adding payment method")));
            } else {
              tempCreditCards.push(postPaymentRes.data);
              setState(prevState => ({
                ...prevState,
                customerCreditCards: tempCreditCards,
                selectedCardId: postPaymentRes.data?.id,
              }));
              void closeAddNewCard();
              dispatch(showSuccess(t("Successfully added payment method")));
            }
          }
        }
      } else {
        dispatch(showError(stripePaymentMethodRes.error.message));
      }
    } catch (e) {
      dispatch(dequeue());
    }
    dispatch(dequeue());
  }

  async function handleBookReservation() {
    if (
      !clientPortalStore?.reservation?.module?.id ||
      !clientPortalStore?.selectedFacility?.id ||
      !clientPortalStore?.reservation?.location?.id ||
      !state?.guests ||
      !state?.durations[state?.durationKey] ||
      !state?.acceptTerms
    ) {
      dispatch(showError(t("Booking error, please try again")));
      void navigateToReservations();
      return;
    }

    const reservationRes = await PostReservationBooking(
      {
        module_id: clientPortalStore?.reservation?.module?.id,
        facility_id: clientPortalStore?.selectedFacility?.id,
        location_id: clientPortalStore?.reservation?.location?.id,
        date: clientPortalStore?.reservation?.segment?.date,
        quantity: state?.guests,
        segment_ids: state.durations[state.durationKey],
        accept_payment_terms: state.acceptTerms,
        customer_payment_method_id: state.selectedCardId ? state?.selectedCardId : undefined,
      },
      true,
    );
    if (reservationRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error booking reservation")));
      void navigateToReservations();
      return;
    }

    dispatch(
      clientPortalUpdate({
        reservation: {
          ...clientPortalStore?.reservation,
          booking: reservationRes?.data,
          bookedSegments: state?.durationSegments[state?.durationKey],
          endTime: moment(state?.endTime, "hh:mm:ss").format("h:mm A"),
        },
      }),
    );

    //Booking successful, go to confirmation page
    history.push(
      `/portal/${clientShortName}/reservations/${
        facilityShortName ?? clientPortalStore?.selectedFacility?.short_name
      }/confirmation?token=${reservationRes?.data?.token as string}&handle=${
        clientPortalStore?.reservation?.module?.handle
      }&location_id=${clientPortalStore?.reservation?.location?.id}`,
    );
  }

  function displayTerms(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    updatePaymentTermsModal({ isOpen: true });
  }

  const disableConfirmButton =
    !state?.guests ||
    !clientPortalStore?.selectedFacility?.id ||
    !clientPortalStore?.reservation ||
    !state.durations ||
    !state?.acceptTerms ||
    (clientPortalStore?.reservation?.segment?.credit_card_required && state?.selectedCardId == null)
      ? true
      : false;

  // Check if card inputs are filled
  const elementsComplete =
    state.elementComplete.cardNumber === true &&
    state.elementComplete.cardExpiry === true &&
    state.elementComplete.cardCvc === true;

  function closeAddNewCard() {
    elements.getElement(CardNumberElement).clear();
    elements.getElement(CardExpiryElement).clear();
    elements.getElement(CardCvcElement).clear();
    setState(prevState => ({ ...prevState, newCreditCardName: "" }));
    void closeNewCardModal();
  }

  return (
    <>
      <div className="client-portal-reservation-booking">
        <ClientPortalNavigation showNavOptions />
        <div className="client-portal-reservation-booking-facility-container">
          <Card>
            <Card.Section>
              <div className="client-portal-reservation-booking-flex-row">
                <span className="facility-logo">
                  {clientPortalStore?.selectedFacility?.logo_source && (
                    <img
                      className="facility-logo"
                      src={clientPortalStore?.selectedFacility?.logo_source}
                      alt={t("Facility Logo")}
                    />
                  )}
                </span>
                {clientPortalStore?.selectedFacility && (
                  <div className="client-portal-reservation-booking-facility-container-group">
                    <p className="client-portal-reservation-booking-primary-text">
                      {clientPortalStore?.selectedFacility?.long_name}
                    </p>
                    <p className="client-portal-reservation-booking-secondary-text">
                      {clientPortalStore?.selectedFacility?.address_line_1}, {clientPortalStore?.selectedFacility?.city}
                      , {clientPortalStore?.selectedFacility?.province_name},{" "}
                      {clientPortalStore?.selectedFacility?.postal}
                    </p>
                  </div>
                )}
              </div>
            </Card.Section>
          </Card>
        </div>
        <div className="client-portal-reservation-booking-main-container">
          <Card>
            <Card.Section>
              <p className="client-portal-reservation-booking-primary-text">
                {clientPortalStore?.reservation?.location?.title} -{" "}
                {moment(clientPortalStore?.reservation?.segment?.start_time, "hh:mm:ss").format("h:mm A")} -{" "}
                {state?.endTime ? moment(state?.endTime, "hh:mm:ss").format("h:mm A") : null}
              </p>
              <p className="client-portal-reservation-booking-tertiary-text">
                {moment(clientPortalStore?.reservation?.segment?.date).format("dddd")}{" "}
                {moment(clientPortalStore?.reservation?.segment?.date).format("LL")}
              </p>

              <div className="client-portal-reservation-booking-flex-row">
                <div className="client-portal-reservation-booking-button-group">
                  <p className="client-portal-reservation-booking-tertiary-text">Duration</p>
                  {state?.durations ? (
                    <Select
                      defaultValue={state?.durationKey}
                      onChange={(value: string) => handleDropDownChange(value, "durationKey")}
                    >
                      {Object?.keys(state?.durations)?.map((durationKey, durationIndex) => {
                        return (
                          <Option key={durationIndex} value={durationKey} name={durationKey}>
                            {durationKey}
                          </Option>
                        );
                      })}
                    </Select>
                  ) : (
                    <div style={{ height: "40px" }}>
                      <Spin />
                    </div>
                  )}
                </div>

                <div className="client-portal-reservation-booking-button-group">
                  <p className="client-portal-reservation-booking-tertiary-text">Guests</p>
                  <Radio.Group name="guests" onChange={handleUpdateGuestsAmount} value={state.guests}>
                    {[...Array(6).keys()].map(index => {
                      return (
                        <Radio.Button key={index} value={index + 1}>
                          {index + 1}
                        </Radio.Button>
                      );
                    })}
                  </Radio.Group>
                </div>
              </div>

              {state?.durationSegments && (
                <div className="client-portal-reservation-booking-button-group">
                  <DataTable
                    columns={[
                      { label: t("Time"), width: "50%" },
                      { label: t("Price"), width: "50%" },
                    ]}
                  >
                    {state.durationSegments[state.durationKey]?.map((segment, segmentIndex) => {
                      if (segmentIndex % 2 === 0) {
                        return (
                          <tr key={segmentIndex}>
                            <td>{moment(segment?.start_time, "hh:mm:ss").format("h:mm A")}</td>
                            <td>
                              {segment?.blocks[0]?.variant_price && (
                                <LocaleCurrency amount={segment?.blocks[0]?.variant_price} /> //TODO: Use currency
                              )}
                            </td>
                          </tr>
                        );
                      }
                    })}
                  </DataTable>
                </div>
              )}
            </Card.Section>
          </Card>

          <Card>
            <Card.Section>
              <p className="client-portal-reservation-booking-tertiary-text">Booking Info</p>

              {clientPortalStore?.reservation?.segment?.credit_card_required ? (
                <div className="reservation-booking-dropdown">
                  <Select
                    label={t("Credit card is required to hold your booking, it will only be charged in accordance to our Payment Terms and Conditions")}
                    defaultValue={state.selectedCardId}
                    onChange={(value: number) => handleCardChange(value)}
                  >
                    <li className="ui-select-dropdown-list-item" onClick={e => updateNewCardModal({ isOpen: true })}>
                      <p className="text-md">Add New Card</p>
                    </li>
                    {state.customerCreditCards?.map((card, index) => {
                      return (
                        <Option key={index} value={card?.id} name={card?.brand}>
                          {capitalize(card?.brand)}
                          {" •••• ".concat(card?.last4)}
                        </Option>
                      );
                    })}
                  </Select>
                </div>
              ) : null}

              <div className="client-portal-reservation-booking-payment-terms-container">
                <Checkbox
                  id="acceptTerms"
                  size="medium"
                  value={state.acceptTerms}
                  checked={state.acceptTerms}
                  onChange={handleCheckboxChange}
                  label={
                    <span>
                      I agree to the{" "}
                      <span onClick={e => displayTerms(e)} className="client-portal-reservation-booking-conditions">
                        Booking and Payment terms of{" "}
                        {clientPortalStore?.selectedFacility?.long_name ?? facilityShortName}
                      </span>
                    </span>
                  }
                />
              </div>

              <div className="client-portal-reservation-booking-confirm-button-group">
                <Button size="medium" type="secondary" onClick={navigateToReservations} block>
                  Back
                </Button>
                <Button
                  size="medium"
                  type="primary"
                  disabled={disableConfirmButton}
                  onClick={handleBookReservation}
                  block
                >
                  Book
                </Button>
              </div>
            </Card.Section>
          </Card>
        </div>
      </div>

      {/* Add new credit card modal */}
      <Portal isMounted={newCardState?.isOpen && windowSize.width > MOBILE_WIDTH}>
        <Sheet
          size="small"
          open={newCardState?.isOpen}
          closable
          title={t("Add New Credit Card")}
          cancelText={t("Cancel")}
          okText={t("Add Card")}
          onOk={handleAddNewCard}
          okDisabled={!elementsComplete ? true : false}
          onCancel={closeAddNewCard}
          backDropCancel={false}
        >
          <div>
            <FormLayout>
              <FormLayout.Group>
                <CardSection onChange={handleCardSectionChange} type="one-row" />
              </FormLayout.Group>
              <FormLayout.Group>
                <Checkbox
                  id="saveCard"
                  size="small"
                  onChange={handleCheckboxChange}
                  label={t("Save card for future bookings")}
                />
              </FormLayout.Group>
            </FormLayout>
          </div>
        </Sheet>
      </Portal>

      <Panel
        open={newCardState?.isOpen && windowSize.width < MOBILE_WIDTH}
        onClose={closeAddNewCard}
        title={t("Add New Credit Card")}
        animate
        primaryAction={{
          onClick: handleAddNewCard,
          label: t("Add Card"),
          disabled: !elementsComplete,
        }}
        secondaryAction={{
          onClick: closeAddNewCard,
          label: t("Cancel"),
        }}
      >
        <div>
          <FormLayout>
            <FormLayout.Group>
              {windowSize.width <= MOBILE_WIDTH && <CardSection onChange={handleCardSectionChange} type="one-row" />}
            </FormLayout.Group>
            <FormLayout.Group>
              <Checkbox
                id="saveCard"
                size="small"
                onChange={handleCheckboxChange}
                label={t("Save card for future bookings")}
              />
            </FormLayout.Group>
          </FormLayout>
        </div>
      </Panel>

      {/* Payment Terms and Conditions Modal */}
      <Portal isMounted={paymentTermsState?.isOpen && windowSize.width > MOBILE_WIDTH}>
        <Sheet
          size="small"
          open={paymentTermsState?.isOpen}
          closable
          title={`${clientPortalStore?.selectedFacility?.long_name} Terms and Conditions`}
          cancelText={t("Close")}
          okText={t("Accept")}
          onOk={() => {
            setState(prevState => ({ ...prevState, acceptTerms: true }));
            void closePaymentTermsModal();
          }}
          onCancel={() => closePaymentTermsModal()}
          backDropCancel={false}
        >
          {clientPortalStore?.reservation?.module?.payment_terms && (
            <>
              <p className="client-portal-reservation-booking-tertiary-text">Payment Terms</p>
              <Markdown markdownText={clientPortalStore?.reservation?.module?.payment_terms ?? ""} />
            </>
          )}

          <br />
          {clientPortalStore?.reservation?.module?.booking_terms && (
            <>
              <p className="client-portal-reservation-booking-tertiary-text">Booking Terms</p>
              <Markdown markdownText={clientPortalStore?.reservation?.module?.booking_terms ?? ""} />
            </>
          )}
        </Sheet>
      </Portal>

      <Panel
        open={paymentTermsState?.isOpen && windowSize.width <= MOBILE_WIDTH}
        onClose={() => closePaymentTermsModal()}
        title={`${clientPortalStore?.selectedFacility?.long_name} Terms and Conditions`}
        animate
        primaryAction={{
          onClick: () => {
            setState(prevState => ({ ...prevState, acceptTerms: true }));
            void closePaymentTermsModal();
          },
          label: t("Accept"),
        }}
        secondaryAction={{
          onClick: () => closePaymentTermsModal(),
          label: t("Close"),
        }}
      >
        {clientPortalStore?.reservation?.module?.payment_terms && (
          <>
            <p className="client-portal-reservation-booking-tertiary-text">Payment Terms</p>
            <Markdown markdownText={clientPortalStore?.reservation?.module?.payment_terms ?? ""} />
          </>
        )}

        <br />
        {clientPortalStore?.reservation?.module?.booking_terms && (
          <>
            <p className="client-portal-reservation-booking-tertiary-text">Booking Terms</p>
            <Markdown markdownText={clientPortalStore?.reservation?.module?.booking_terms ?? ""} />
          </>
        )}
      </Panel>
    </>
  );
}
